import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/authStore";
import { type SignInData, type SignUpData } from "@/services/authApi";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// Auth user data interface
export interface AuthUser {
  id: string;
  email: string;
  role?: string;
  metadata?: any;
}

// Hook for current user data
// export function useCurrentUser() {
//   const { session, initialized } = useAuthStore();

//   return useQuery({
//     queryKey: ["auth", "me"],
//     queryFn: async (): Promise<AuthUser> => {
//       if (!session?.access_token) {
//         throw new Error("No session available");
//       }
//       return apiClient
//         .get<{ user: AuthUser }>("/v1/auth/me")
//         .then((data) => data.user);
//     },
//     enabled: !!session?.access_token && initialized,
//     staleTime: 5 * 60 * 1000, // 5 minutes
//     retry: false,
//   });
// }

// Hook for token verification
export function useVerifyToken() {
  const { session } = useAuthStore();

  return useQuery({
    queryKey: ["auth", "verify", session?.access_token],
    queryFn: async (): Promise<AuthUser> => {
      if (!session?.access_token) {
        throw new Error("No token available");
      }
      return apiClient.post<AuthUser>("/v1/auth/verify");
    },
    enabled: !!session?.access_token,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: false,
  });
}

// Hook for sign in mutation
export function useSignIn() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { signIn, clearError } = useAuthStore();

  return useMutation({
    mutationFn: async (data: SignInData) => {
      clearError();
      const result = await signIn(data.email, data.password);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth"] });
      toast.success("Logged in successfully!", {
        position: "bottom-right",
      });
      navigate("/admin");
    },
    onError: (error: Error) => {
      toast.error(error.message, {
        position: "bottom-right",
      });
    },
  });
}

// Hook for sign up mutation
export function useSignUp() {
  const navigate = useNavigate();
  const { signUp, clearError } = useAuthStore();

  return useMutation({
    mutationFn: async (data: SignUpData) => {
      clearError();
      const result = await signUp(
        data.email,
        data.password,
        data.firstName,
        data.lastName
      );
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result;
    },
    onSuccess: () => {
      toast.success(
        "Account created successfully! Please check your email to verify your account.",
        {
          position: "bottom-right",
        }
      );
      navigate("/login");
    },
    onError: (error: Error) => {
      toast.error(error.message, {
        position: "bottom-right",
      });
    },
  });
}

// Hook for Google sign in mutation
export function useGoogleSignIn() {
  const { signInWithGoogle, clearError } = useAuthStore();

  return useMutation({
    mutationFn: async () => {
      clearError();
      const result = await signInWithGoogle();
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result;
    },
    onError: (error: Error) => {
      toast.error(error.message, {
        position: "bottom-right",
      });
    },
  });
}

// Hook for Google sign in mutation
export function useGoogleSignInButton() {
  const navigate = useNavigate();
  const { signInWithGoogleButton, clearError } = useAuthStore();

  return useMutation({
    mutationFn: async (response: any) => {
      clearError();
      const result = await signInWithGoogleButton(response);
      if (result.error) {
        throw new Error(result.error.message);
      }
      console.log("Successfully signed in with Google");
      console.log("Result", result);
      return result;
    },
    onSuccess: () => {
      toast.success("Logged in successfully!", {
        position: "bottom-right",
      });
      console.log("Navigating to admin");
      navigate("/admin");
    },
    onError: (error: Error) => {
      toast.error(error.message, {
        position: "bottom-right",
      });
    },
  });
}

// Hook for sign out mutation
export function useSignOut() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { signOut } = useAuthStore();

  return useMutation({
    mutationFn: async () => {
      await signOut();
    },
    onSuccess: () => {
      queryClient.clear(); // Clear all cached data
      toast.success("Logged out successfully", {
        position: "bottom-right",
      });
      navigate("/login");
    },
    onError: (_error: Error) => {
      toast.error("Failed to logout", {
        position: "bottom-right",
      });
    },
  });
}

// Hook for session refresh
export function useRefreshSession() {
  const queryClient = useQueryClient();
  const { session } = useAuthStore();

  return useMutation({
    mutationFn: async () => {
      if (!session?.refresh_token) {
        throw new Error("No refresh token available");
      }
      return apiClient.post("/v1/auth/refresh", {
        refresh_token: session.refresh_token,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
  });
}

// Combined auth hook for convenience
export function useAuth() {
  const { user, session, loading, initialized, error, signingOut, localUser } =
    useAuthStore();
  // const currentUserQuery = useCurrentUser();
  const signInMutation = useSignIn();
  const signUpMutation = useSignUp();
  const googleSignInMutation = useGoogleSignIn();
  const googleSignInButtonMutation = useGoogleSignInButton();
  const signOutMutation = useSignOut();

  return {
    // State
    user,
    session,
    loading: loading,
    initialized,
    error,
    signingOut,
    isAuthenticated: !!user && !!session,
    localUser,

    // Current user data
    // currentUser: currentUserQuery.data,
    // currentUserLoading: currentUserQuery.isLoading,
    // currentUserError: currentUserQuery.error,

    // Mutations
    signIn: signInMutation.mutateAsync,
    signUp: signUpMutation.mutateAsync,
    signInWithGoogle: googleSignInMutation.mutateAsync,
    signInWithGoogleButton: googleSignInButtonMutation.mutateAsync,
    signOut: signOutMutation.mutateAsync,

    // Mutation states
    isSigningIn: signInMutation.isPending,
    isSigningUp: signUpMutation.isPending,
    isSigningOut: signOutMutation.isPending,
    isSigningInWithGoogle: googleSignInMutation.isPending,
    isSigningInWithGoogleButton: googleSignInButtonMutation.isPending,
  };
}
